/**
 * Pip's looks (skins) at runtime. The art build bakes classic Pip only (`pip_<action>` from the
 * atlases); a look is drawn on the device the first time Pip wears it, and only that look:
 * @mp/art/pip (a lazy chunk) writes each action's frames into one SVG (`pipSheetSvg`), the
 * browser rasterises it into a canvas, and the canvas becomes a sprite sheet animated as
 * `pip_<action>@<skin>`. Pip stays kraft until every action of the look is in, so a round never
 * mixes two looks. Wearing another look drops the previous one's textures (~20 MB of GPU memory).
 */
import type * as Phaser from 'phaser';

/** The rig's actions (packages/art pip-rig.ts `PIP_ACTIONS`), the ones in play and on the map first. */
export const PIP_LOOK_ACTIONS = ['idle', 'catch', 'pop', 'panic', 'win', 'worried', 'slump', 'celebrate', 'wave', 'sleep'] as const;
const LOOPS = new Set<string>(['idle', 'worried', 'panic', 'sleep']);

/** The textures and animations of one Phaser game (both are game-wide, shared by its scenes). */
export interface LookHost {
  textures: Pick<Phaser.Textures.TextureManager, 'exists' | 'addSpriteSheet' | 'remove'>;
  anims: Pick<Phaser.Animations.AnimationManager, 'exists' | 'create' | 'remove' | 'generateFrameNumbers'>;
}

/** SVG markup → something a texture can be made of, `w`×`h` px. */
export type Rasterise = (svg: string, w: number, h: number) => Promise<HTMLCanvasElement>;

/** The animation of an action in a look; classic is the atlas' own. */
export function pipAnimKey(action: string, skin: string): string {
  return skin === 'classic' ? `pip_${action}` : `pip_${action}@${skin}`;
}

const sheetKey = (skin: string, action: string) => `pip_look_${skin}_${action}`;

/** Columns of an action's sheet: at most 5 cells (1280 px) a row, and no empty cells for the rig's 6–12 frames. */
export function sheetCols(frames: number): number {
  return Math.ceil(frames / Math.ceil(frames / 5));
}

/** An SVG as an `<img>` source (DOM previews use it too). */
export const svgDataUrl = (svg: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/** The browser's own SVG renderer: the markup as an image, drawn once into a canvas (a bitmap for WebGL and Canvas alike). */
export async function rasterise(svg: string, w: number, h: number): Promise<HTMLCanvasElement> {
  const img = new Image(w, h);
  img.src = svgDataUrl(svg);
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
  return canvas;
}

const loads = new WeakMap<LookHost, Map<string, Promise<boolean>>>();

function loadsOf(host: LookHost): Map<string, Promise<boolean>> {
  let m = loads.get(host);
  if (!m) loads.set(host, (m = new Map()));
  return m;
}

/**
 * Draw a look's animations once per game. Resolves true once every action is in; false when the
 * look is unknown or could not be drawn (Pip stays kraft; the next wear tries again).
 */
export function loadLook(host: LookHost, skin: string, raster: Rasterise = rasterise): Promise<boolean> {
  if (skin === 'classic') return Promise.resolve(true);
  const m = loadsOf(host);
  const hit = m.get(skin);
  if (hit) return hit;
  const p: Promise<boolean> = draw(host, skin, raster).catch((e: unknown) => {
    if (m.get(skin) === p) m.delete(skin);
    console.warn(`[market-pop] Pip look ${skin} not drawn`, e);
    return false;
  });
  m.set(skin, p);
  return p;
}

async function draw(host: LookHost, skin: string, raster: Rasterise): Promise<boolean> {
  const art = await import('@mp/art/pip');
  if (!art.isPipSkin(skin)) return false;
  for (const action of PIP_LOOK_ACTIONS) {
    const poses = art.pipFrames(action);
    const cols = sheetCols(poses.length);
    const key = sheetKey(skin, action);
    if (!host.textures.exists(key)) {
      const sheet = await raster(art.pipSheetSvg(poses, skin, cols), cols * art.PIP_SIZE, Math.ceil(poses.length / cols) * art.PIP_SIZE);
      if (!loadsOf(host).has(skin)) return false; // dropped while drawing: another look was worn
      // Phaser takes a canvas wherever it takes an image (TextureSource)
      if (!host.textures.exists(key)) host.textures.addSpriteSheet(key, sheet as unknown as HTMLImageElement, { frameWidth: art.PIP_SIZE, frameHeight: art.PIP_SIZE, endFrame: poses.length - 1 });
    }
    const anim = pipAnimKey(action, skin);
    if (!host.anims.exists(anim))
      host.anims.create({ key: anim, frames: host.anims.generateFrameNumbers(key, { start: 0, end: poses.length - 1 }), frameRate: 12, repeat: LOOPS.has(action) ? -1 : 0 });
  }
  return true;
}

/** True once a look's animations are all in (classic always is). */
export function lookReady(host: LookHost, skin: string): boolean {
  return skin === 'classic' || PIP_LOOK_ACTIONS.every((a) => host.anims.exists(pipAnimKey(a, skin)));
}

/** Free every drawn look but `keep` (call once nothing plays them: Pips follow the one look the player wears). */
export function dropLooks(host: LookHost, keep: string): void {
  const m = loadsOf(host);
  for (const skin of [...m.keys()]) {
    if (skin === keep) continue;
    m.delete(skin);
    for (const action of PIP_LOOK_ACTIONS) {
      if (host.anims.exists(pipAnimKey(action, skin))) host.anims.remove(pipAnimKey(action, skin));
      if (host.textures.exists(sheetKey(skin, action))) host.textures.remove(sheetKey(skin, action));
    }
  }
}
